import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import TicksList from '@components/dashboard/components/TicksList';
import ConsultationPriceDetails from '@components/products/ConsultationPriceDetails';
import { CartItemType, PageSlice, ProductCode } from '@contracts';
import IconTickGold from '@images/icon-tick-gold-bg.svg';
import IconTickGreen from '@images/icon-tick-green-bg.svg';
import { createSimpleCartItemForProductCode } from '@utils/cart';
import { getNewProductPrice } from '@utils/price';
import AddToCart from './AddToCart';
import Colors from './Colors';
import { TextVariant, Typography } from './design/Typography';
import LinkButton from './LinkButton';
import ReplacedText from './ReplacedText';
import TwoColumn from './TwoColumn';

interface Props {
  slice: PageSlice;
}

const ConsultationsExplained: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { state } = useContext(Context);

  const { currency, country } = state;
  const [productPrices, setProductPrices] = useState<{ [productCode: string]: string | null }>({});
  const GROUP_IMG = 'https://images.prismic.io/ecommerce-website/ZnrMN5bWFbowe15j_NurseConsultationImg2.png?auto=format,compress';
  const consultationItems = [
    {
      time: '30min',
      productCode: 'ppc_advanced',
      title: 'consultations.consultations-explained-item-1-title',
      description: 'consultations.consultations-explained-item-1-description',
      includes: [
        t('consultations.consultations-explained-item-1-includes-1'),
        t('consultations.consultations-explained-item-1-includes-2'),
        t('consultations.consultations-explained-item-1-includes-3'),
        t('consultations.consultations-explained-item-1-includes-4'),
        t('consultations.consultations-explained-item-1-includes-5')
      ]
    },
    {
      time: '30min',
      productCode: 'vms_essential_consultation_basic',
      title: 'consultations.consultations-explained-item-2-title',
      description: 'consultations.consultations-explained-item-2-description',
      includes: [
        t('consultations.consultations-explained-item-2-includes-1'),
        t('consultations.consultations-explained-item-2-includes-2'),
        t('consultations.consultations-explained-item-2-includes-3'),
        t('consultations.consultations-explained-item-2-includes-4')
      ]
    }
  ];
  const focusItem = {
    time: '30min',
    productCode: 'consultation_sexual_health',
    includes: [t('consultation-explained.includes-1'), t('consultation-explained.includes-2'), t('consultation-explained.includes-3')]
  };

  useEffect(() => {
    const fetchPrices = async () => {
      const prices: { [productCode: string]: string | null } = {};
      const productCodes = consultationItems.map((item) => item.productCode);
      productCodes.push(focusItem.productCode);
      for (const productCode of productCodes) {
        if (productCode) {
          const price = await getPriceForProduct(productCode);
          prices[productCode] = price !== -1 ? price : null;
        }
      }
      setProductPrices(prices);
    };

    fetchPrices();
  }, [currency]);

  const getPriceForProduct = async (productCode: string) => {
    const price = await getNewProductPrice(currency, country, productCode);
    return price ?? -1;
  };

  const renderConsultationItem = (item, index) => {
    const cartItemPrice = Number(productPrices[item.productCode]);
    const ppcLink = '/ppc-questionnaire/';
    const quantity = 1;
    const editable = true;
    const cartItem =
      cartItemPrice > -1 ? createSimpleCartItemForProductCode(item.productCode, quantity, cartItemPrice, editable, CartItemType.Consultation) : null;
    const isPPCItem = item.productCode === ProductCode.PPCAdvanced;

    return (
      <ConsultationsItem key={index}>
        <Header>
          <ReplacedText as={Typography} $variant={TextVariant.BodyBoldLG}>
            {t(item.title)}
          </ReplacedText>
          <ConsultationPriceDetails
            currencySymbol={currency.symbol}
            price={cartItemPrice}
            duration={item.time}
            datatestid="consultation-upsell-price"
          />
          <ReplacedText as={ConsultationBody} $variant={TextVariant.BodyRegularMD}>
            {t(item.description)}
          </ReplacedText>
        </Header>
        <TicksList data={item.includes} columns={1} Icon={IconTickGold} />
        {isPPCItem ? (
          <StyledLinkButton to={ppcLink} backgroundColor={Colors.paleGold} textColor={Colors.navyBlue}>
            {t('components.get-started')}
          </StyledLinkButton>
        ) : (
          <AddToCart cartItem={cartItem} checkoutRedirect={false} textKey="components.buy-now" />
        )}
      </ConsultationsItem>
    );
  };

  const renderConsultationProductItem = () => {
    const cartItemPrice = Number(productPrices[focusItem.productCode]);
    const quantity = 1;
    const editable = true;
    const cartItem =
      cartItemPrice > -1
        ? createSimpleCartItemForProductCode(focusItem.productCode, quantity, cartItemPrice, editable, CartItemType.Consultation)
        : null;

    return (
      <>
        <TimePriceWrap>
          {focusItem.productCode ? (
            <ConsultationPriceDetails currencySymbol={currency.symbol} price={cartItemPrice} duration={focusItem.time} />
          ) : null}
        </TimePriceWrap>
        <TicksList data={focusItem.includes} columns={1} Icon={IconTickGreen} />
        <ButtonWrapper>
          <AddToCart cartItem={cartItem} checkoutRedirect={false} textKey="free-consultations.book-consultation" />
        </ButtonWrapper>
      </>
    );
  };

  return (
    <>
      <UpsellContainer>
        <TwoColumn breakpoint="1024px" equal mobileReverse>
          <ImageWrapper>
            <img src={GROUP_IMG} />
          </ImageWrapper>
          <TextWrapper>
            <TextTop>
              <TopTitleWrapper>
                <ReplacedText as={Typography} $variant={TextVariant.Heading4} $mobileVariant={TextVariant.Heading6}>
                  {t('consultation-explained.title')}
                </ReplacedText>
              </TopTitleWrapper>
              <DescWrapper>
                <ReplacedText as={Typography} $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
                  {t('consultation-explained.description')}
                </ReplacedText>
              </DescWrapper>
            </TextTop>
            {renderConsultationProductItem()}
          </TextWrapper>
        </TwoColumn>
      </UpsellContainer>
      <ExplainedContainer id="consultations-explained">
        <ConsultationsContainer>
          <ConsultationIntro>
            <ReplacedText as={Typography} $variant={TextVariant.Heading5} $mobileVariant={TextVariant.Heading6}>
              {t('consultation-explained.learn-more-title')}
            </ReplacedText>
            <ReplacedText as={Typography} $variant={TextVariant.BodyRegularLG} $mobileVariant={TextVariant.BodyRegularMD}>
              {t('consultation-explained.learn-more-description')}
            </ReplacedText>
          </ConsultationIntro>
          {consultationItems.map((item, index) => renderConsultationItem(item, index))}
        </ConsultationsContainer>
      </ExplainedContainer>
    </>
  );
};

export default ConsultationsExplained;

const UpsellContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  background: ${Colors.cream};
  border-radius: 2px;
`;

const ExplainedContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 96px 120px;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  background: ${Colors.paleGold25};

  @media (max-width: 768px) {
    padding: 56px 25px;
    align-items: flex-start;
    gap: 24px;
  }

  & h4 {
    font-family: 'Domaine';
    font-size: 40px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.4px;
    color: ${Colors.navyBlue};

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
`;

const TextWrapper = styled.div`
  max-width: 50%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  flex-grow: 1;
  padding: 0 120px;
  color: ${Colors.navyBlue};

  @media (max-width: 1300px) {
    padding: 20px 60px;
  }

  @media (max-width: 1024px) {
    padding: 40px 25px;
    max-width: 100%;
    gap: 16px;
  }
`;

const TextTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  @media (max-width: 1024px) {
    gap: 8px;
  }
`;

const DescWrapper = styled.div``;

const TopTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  display: flex;

  @media (max-width: 1024px) {
    a {
      width: 100%;
      border-radius: 2px;
    }
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  max-width: 50%;
  flex-grow: 1;

  img {
    width: 100%;
    height: auto;
  }

  @media (max-width: 1024px) {
    overflow: hidden;
    width: 100%;
    max-width: 100%;

    img {
      max-width: 100%;
    }
  }
`;

const ConsultationsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
  max-width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const ConsultationsItem = styled.div`
  display: flex;
  width: 420px;
  max-width: 100%;
  padding: 32px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-radius: 5px;
  border: 1px solid ${Colors.paleGold};
  background: ${Colors.cream};
  border-radius: 5px;

  & button {
    width: 100%;
  }

  &:nth-child(3) {
    border: 2px solid ${Colors.paleGold};
    background: ${Colors.paleGold25};
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  & h5 {
    font-family: 'Domaine';
    font-size: 32px;
    color: ${Colors.navyBlue};
    letter-spacing: -0.32px;

    @media (max-width: 768px) {
      font-size: 20px;
      letter-spacing: -0.2px;
    }
  }
`;

const TimePriceWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;
  align-self: stretch;

  & button {
    height: 44px;
    padding: 0px 15px;
  }
`;

const ConsultationIntro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  max-width: 433px;
  margin-right: 20px;
`;

const StyledLinkButton = styled(LinkButton)`
  width: 100%;
`;

const ConsultationBody = styled(Typography)`
  @media (max-width: 768px) {
    display: none;
  }
`;
